import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import {
  api,
  type Attachment,
  type CannedResponse,
  type ConversationDetail,
  type ConversationRow,
  type InboxCounts,
  type Message,
  type Operator,
  type PageVisit,
  type TeamMember,
} from "../../api";
import type { AgentSocket } from "../../socket";
import { ImageViewer } from "../../components/Media";
import { useDialogs } from "../../components/Dialog";
import { mergeMessages } from "../../lib/thread";
import { visitorLabel } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { isOperatorAway, notify, showUnread } from "../../lib/notify";
import {
  ConversationList,
  INBOX_MAX,
  INBOX_PAGE,
  canShowMore,
  tabQuery,
  type BulkAction,
  type InboxTab,
} from "./ConversationList";
import { ConversationHeader } from "./ConversationPane";
import { Thread } from "./Thread";
import { Composer } from "./Composer";
import { ContextPanel } from "./ContextPanel";
import { HandoverDialog } from "./HandoverDialog";
import { suggestCanned } from "../../lib/suggest";
import { apiErrorText, errorCodeOf } from "../../lib/apiError";
import { askBan, askDeleteConversation, askMessageDelete, askMessageEdit, askPurge, askTag, askVisitorDetails } from "./asks";

/**
 * The inbox: the screen an operator lives in.
 *
 * It owns the state; the pieces around it only draw. Live events refresh the
 * list and counts (debounced, since one customer message can produce several
 * events), and the open thread takes new messages directly so a reply appears
 * without a round trip.
 */

interface Props {
  operator: Operator;
  socket: AgentSocket;
  selectedId: number | null;
  select: (id: number | null) => void;
  onWaiting: (count: number) => void;
}

/**
 * A freshly loaded newest page, joined to what is already on screen.
 *
 * The open conversation is re-read after every action on it, and the answer
 * is always its newest page. Replacing the thread with that would throw away
 * the older pages an operator had scrolled back through — so anything older
 * than the new page is kept, as long as it is the same conversation.
 */
export function keepEarlier(current: Message[], page: Message[], conversationId: number): Message[] {
  if (current.length === 0 || current[0]!.conversationId !== conversationId) return page;
  const firstNew = page.find((m) => m.id > 0)?.id;
  if (firstNew === undefined) return page;
  return [...current.filter((m) => m.id > 0 && m.id < firstNew), ...page];
}

export function InboxView({ operator, socket, selectedId, select, onWaiting }: Props): JSX.Element {
  const { t, lang } = useI18n();
  const dialogs = useDialogs();
  const [tab, setTab] = useState<InboxTab>("active");
  const [query, setQuery] = useState("");
  const [siteId, setSiteId] = useState<number | null>(null);
  const [tag, setTag] = useState("");
  const [limit, setLimit] = useState(INBOX_PAGE);
  const [sites, setSites] = useState<Array<{ id: number; name: string }>>([]);
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<InboxCounts | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasEarlier, setHasEarlier] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [canned, setCanned] = useState<CannedResponse[]>([]);
  const [visitorTyping, setVisitorTyping] = useState(false);
  /** What the visitor has typed but not sent, live; never stored anywhere. */
  const [visitorDraft, setVisitorDraft] = useState<string | null>(null);
  const [handover, setHandover] = useState<TeamMember | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  const [toast, setToast] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const [now, setNow] = useState(Date.now());
  /** Null while not selecting; otherwise the conversations ticked for a bulk action. */
  const [picked, setPicked] = useState<Set<number> | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;
  const tRef = useRef(t);
  tRef.current = t;
  /** The conversation whose older pages are on screen, so a re-read does not reset "is there more?". */
  const walkedBack = useRef<number | null>(null);

  const flash = useCallback((text: string, tone: "ok" | "error" = "error") => {
    setToast({ tone, text });
    window.setTimeout(() => setToast((current) => (current?.text === text ? null : current)), 4000);
  }, []);

  // Erasing a conversation or a person is admin work; ordinary operators see
  // the actions disabled rather than missing, so they know to ask.
  const isAdmin = operator.role === "owner" || operator.role === "admin";

  const self = useMemo(
    () => ({
      id: operator.id,
      displayName: operator.displayName,
      avatarUrl: operator.avatarUrl ?? null,
      color: operator.color ?? "var(--sea)",
    }),
    [operator],
  );

  // ---------------------------------------------------------------- loading

  const loadList = useCallback(async () => {
    try {
      const res = await api.conversations({
        ...tabQuery(tab),
        q: query.trim() || undefined,
        siteId: siteId ?? undefined,
        tag: tag.trim() || undefined,
        limit,
      });
      setRows(res.conversations);
    } catch {
      flash(tRef.current.inbox.listFailed);
    } finally {
      setLoading(false);
    }
  }, [tab, query, siteId, tag, limit, flash]);

  const loadCounts = useCallback(async () => {
    try {
      const next = await api.inboxCounts();
      setCounts(next);
      onWaiting(next.pending);
    } catch {
      /* the list shows the error; counts are a convenience */
    }
  }, [onWaiting]);

  const loadDetail = useCallback(
    async (id: number) => {
      try {
        const res = await api.conversation(id);
        if (selectedRef.current !== id) return;
        setDetail(res);
        setMessages((current) => keepEarlier(current, res.messages, id));
        // Once the operator has walked back through this thread, "is there
        // more above?" is the last walk's answer, not the newest page's.
        if (walkedBack.current !== id) setHasEarlier(res.hasEarlier ?? false);
        socket.subscribe(id);
        const last = res.messages[res.messages.length - 1];
        if (last && last.id > 0) {
          void api.markRead(id, last.id).then(() => {
            void loadList();
          });
        }
      } catch {
        flash(tRef.current.inbox.openFailed);
        select(null);
      }
    },
    [socket, flash, select, loadList],
  );

  // The search and the tag wait for a pause in typing; everything else loads at once.
  useEffect(() => {
    const timer = window.setTimeout(() => void loadList(), query || tag ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [loadList, query, tag]);

  useEffect(() => {
    void loadCounts();
    void api
      .team()
      .then((r) => setTeam(r.operators))
      .catch(() => undefined);
    void api
      .canned()
      .then((r) => setCanned(r.canned))
      .catch(() => undefined);
    void api
      .sites()
      .then((r) => setSites(r.sites.map((s) => ({ id: s.id, name: s.name }))))
      .catch(() => undefined);
  }, [loadCounts]);

  // Opening a conversation subscribes to it and marks it read, so this runs
  // when the selection changes and never merely because a callback did —
  // otherwise one unstable prop turns into a subscribe storm the server cuts off.
  const loadDetailRef = useRef(loadDetail);
  loadDetailRef.current = loadDetail;
  useEffect(() => {
    setVisitorTyping(false);
    setVisitorDraft(null);
    setInfoOpen(false);
    setLoadingEarlier(false);
    walkedBack.current = null;
    if (selectedId) void loadDetailRef.current(selectedId);
    else {
      setDetail(null);
      setMessages([]);
      setHasEarlier(false);
    }
  }, [selectedId]);

  // Waiting times on the list are relative; keep them honest.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  /** A different list: start again from the first page, with nothing ticked. */
  function resetList(): void {
    setLoading(true);
    setLimit(INBOX_PAGE);
    setPicked((current) => (current === null ? null : new Set()));
  }

  async function loadEarlier(): Promise<void> {
    const id = selectedId;
    const first = messages.find((m) => m.id > 0);
    if (!id || !first || loadingEarlier) return;
    setLoadingEarlier(true);
    try {
      const res = await api.earlierMessages(id, first.id);
      if (selectedRef.current !== id) return;
      walkedBack.current = id;
      setMessages((current) => mergeMessages(current, res.messages));
      setHasEarlier(res.hasEarlier);
    } catch (err) {
      flash(apiErrorText(err, t, t.common.actionFailed));
    } finally {
      setLoadingEarlier(false);
    }
  }

  // ------------------------------------------------------------ live events

  const refreshSoon = useRef<number | null>(null);
  const refresh = useCallback(() => {
    if (refreshSoon.current !== null) window.clearTimeout(refreshSoon.current);
    refreshSoon.current = window.setTimeout(() => {
      refreshSoon.current = null;
      void loadList();
      void loadCounts();
    }, 300);
  }, [loadList, loadCounts]);

  // A message that arrived in the open conversation while the operator was
  // away stays unread — that is what lights the badge — until they are back,
  // and is marked read then.
  const unreadWhileAway = useRef<{ conversationId: number; messageId: number } | null>(null);
  useEffect(() => {
    const back = (): void => {
      const pending = unreadWhileAway.current;
      if (!pending || isOperatorAway()) return;
      unreadWhileAway.current = null;
      if (pending.conversationId !== selectedRef.current) return;
      void api.markRead(pending.conversationId, pending.messageId).then(() => refresh());
    };
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", back);
    return () => {
      window.removeEventListener("focus", back);
      document.removeEventListener("visibilitychange", back);
    };
  }, [refresh]);

  useEffect(() => {
    return socket.on((event) => {
      const tt = tRef.current;
      if (event.t === "socket.status") {
        // Back after a drop: whatever happened meanwhile was never delivered,
        // so the list and the open thread are read again rather than trusted.
        if (event.data.connected) {
          refresh();
          if (selectedRef.current) void loadDetail(selectedRef.current);
        }
        return;
      }
      if (event.t === "conv.new") {
        const data = event.data as { conversationId: number; message?: Message };
        notify({
          title: tt.notify.newChat,
          body: data.message?.body || tt.notify.image,
          tag: `conv-${data.conversationId}`,
          hash: `#/inbox/${data.conversationId}`,
          onClick: () => select(data.conversationId),
        });
        refresh();
        return;
      }
      if (event.t === "msg.new") {
        const payload = event.data as { conversationId?: number; message?: Message } & Message;
        const conversationId = payload.conversationId ?? payload.message?.conversationId;
        const message = payload.message ?? (payload as Message);
        const isOpen = conversationId !== undefined && conversationId === selectedRef.current;
        if (isOpen) {
          setMessages((current) => mergeMessages(current, [message]));
          if (message.kind === "visitor") {
            setVisitorTyping(false);
            setVisitorDraft(null);
            if (isOperatorAway()) unreadWhileAway.current = { conversationId, messageId: message.id };
            else void api.markRead(conversationId, message.id);
          }
        }
        // A message in the conversation on screen needs no sound — unless the
        // operator is not looking at the screen at all.
        if (message.kind === "visitor" && conversationId && (!isOpen || isOperatorAway())) {
          notify({
            title: tt.notify.newMessage(tt.visitor(conversationId)),
            body: message.body || tt.notify.image,
            tag: `conv-${conversationId}`,
            hash: `#/inbox/${conversationId}`,
            onClick: () => select(conversationId),
          });
        }
        refresh();
        return;
      }
      if (event.t === "msg.edit") {
        // Bare from the conversation's own room, wrapped from the site's.
        const payload = event.data as { message?: Message } & Message;
        const message = payload.message ?? (payload as Message);
        if (message.conversationId === selectedRef.current) {
          // Only a message already on screen: an edit is not a new message.
          setMessages((current) => (current.some((m) => m.id === message.id) ? mergeMessages(current, [message]) : current));
        }
        refresh();
        return;
      }
      if (event.t === "msg.delete") {
        const { messageId } = event.data;
        setMessages((current) => (current.some((m) => m.id === messageId) ? current.filter((m) => m.id !== messageId) : current));
        refresh();
        return;
      }
      if (event.t === "agent.presence") {
        const { operatorId, presence } = event.data;
        setTeam((current) => current.map((m) => (m.id === operatorId ? { ...m, presence } : m)));
        return;
      }
      if (event.t === "draft") {
        if (event.data.conversationId === selectedRef.current) setVisitorDraft(event.data.text.trim() ? event.data.text : null);
        return;
      }
      if (event.t === "assigned") {
        const data = event.data;
        const title = data.by ? tt.inbox.assignedBy(data.by.displayName) : tt.inbox.assignedAuto;
        notify({
          title,
          body: data.note || tt.visitor(data.conversationId),
          tag: `assigned-${data.conversationId}`,
          hash: `#/inbox/${data.conversationId}`,
          onClick: () => select(data.conversationId),
        });
        flash(data.note ? `${title}: ${data.note}` : title, "ok");
        refresh();
        return;
      }
      if (event.t === "mention") {
        const data = event.data;
        notify({
          title: tt.inbox.mentionedBy(data.from.displayName),
          body: data.excerpt,
          tag: `mention-${data.messageId}`,
          hash: `#/inbox/${data.conversationId}`,
          onClick: () => select(data.conversationId),
        });
        flash(`${tt.inbox.mentionedBy(data.from.displayName)}: ${data.excerpt}`, "ok");
        return;
      }
      if (event.t === "typing") {
        const data = event.data as { conversationId?: number; who?: string; typing?: boolean };
        if (data.who === "visitor" && data.conversationId === selectedRef.current) {
          setVisitorTyping(Boolean(data.typing));
          if (data.typing) window.setTimeout(() => setVisitorTyping(false), 4000);
        }
        return;
      }
      if (event.t === "conv.update") {
        const data = event.data as { conversationId?: number; page?: { url: string; title: string | null } };
        if (data.conversationId && data.conversationId === selectedRef.current) {
          if (data.page) {
            const page: PageVisit = { url: data.page.url, title: data.page.title, visitedAt: new Date().toISOString() };
            setDetail((d) => (d ? { ...d, pages: [page, ...d.pages].slice(0, 20) } : d));
          } else {
            void loadDetail(data.conversationId);
          }
        }
        refresh();
      }
    });
  }, [socket, refresh, loadDetail, select, flash]);

  // The tab title and icon are how an operator on another tab notices a waiting customer.
  const unread = useMemo(() => rows.reduce((sum, row) => sum + row.unread, 0), [rows]);
  useEffect(() => {
    showUnread(unread, t.common.appTitle, t.common.appTitleUnread);
  }, [unread, t]);

  // ---------------------------------------------------------------- actions

  /**
   * An erasure, as opposed to an edit.
   *
   * Separate from `run` because `run` reloads the open conversation
   * afterwards — which, for a thread that no longer exists, is a 404 and an
   * error toast on top of a delete that actually worked. This closes the pane
   * first and only then refreshes the list.
   */
  async function erase<T>(action: () => Promise<T>, done: (result: T) => string): Promise<void> {
    try {
      const result = await action();
      select(null);
      flash(done(result), "ok");
      refresh();
    } catch (err) {
      flash(apiErrorText(err, t, t.common.actionFailed));
    }
  }

  async function run(action: () => Promise<unknown>, done?: string): Promise<void> {
    try {
      await action();
      if (done) flash(done, "ok");
      if (selectedId) await loadDetail(selectedId);
      refresh();
    } catch (err) {
      flash(apiErrorText(err, t, t.common.actionFailed));
    }
  }

  // Saved replies that fit — only while the customer has the last word.
  const suggestions = useMemo(() => {
    const visible = messages.filter((m) => m.kind !== "note");
    const last = visible[visible.length - 1];
    return last?.kind === "visitor" ? suggestCanned(canned, last.body) : [];
  }, [messages, canned]);

  async function openTicket(id: number): Promise<void> {
    try {
      const res = await api.openTicket(id);
      flash(t.inbox.ticketCreated(res.ticket.id), "ok");
      await loadDetail(id);
    } catch (err) {
      const code = errorCodeOf(err) ?? "";
      flash(t.inbox.ticketErrors[code] ?? apiErrorText(err, t, t.common.actionFailed));
    }
  }

  async function editMessage(message: Message): Promise<void> {
    const body = await askMessageEdit(dialogs, t, message);
    if (body === null) return;
    try {
      const res = await api.editMessage(message.conversationId, message.id, body);
      if (selectedRef.current === message.conversationId) setMessages((current) => mergeMessages(current, [res.message]));
      refresh();
    } catch (err) {
      flash(apiErrorText(err, t, t.common.actionFailed));
    }
  }

  async function deleteMessage(message: Message): Promise<void> {
    if (!(await askMessageDelete(dialogs, t, message))) return;
    try {
      await api.deleteMessage(message.conversationId, message.id);
      setMessages((current) => current.filter((m) => m.id !== message.id));
      flash(t.inbox.messageDeleted, "ok");
      refresh();
    } catch (err) {
      flash(apiErrorText(err, t, t.common.actionFailed));
    }
  }

  /**
   * One action over every ticked conversation.
   *
   * One request at a time through the same endpoints a single click uses, so
   * each conversation is checked against the operator's sites exactly as it
   * would be alone, and a failure half-way through is counted, not hidden.
   */
  async function bulk(action: BulkAction): Promise<void> {
    const ids = visiblePicked ? [...visiblePicked] : [];
    if (ids.length === 0) return;
    if (action === "delete") {
      const sure = await dialogs.confirm({
        title: t.inbox.bulkDeleteTitle(ids.length),
        body: t.inbox.bulkDeleteBody,
        confirmLabel: t.common.delete,
        danger: true,
      });
      if (!sure) return;
    }
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        if (action === "resolve") await api.setStatus(id, "resolved");
        else if (action === "assign") await api.assign(id, operator.id);
        else await api.deleteConversation(id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    setPicked(new Set());
    if (selectedId && ids.includes(selectedId)) {
      if (action === "delete") select(null);
      else void loadDetail(selectedId);
    }
    if (failed > 0) flash(t.inbox.bulkPartial(ok, failed));
    else flash(action === "delete" ? t.inbox.bulkDeleted(ok) : t.inbox.bulkDone(ok), "ok");
    refresh();
  }

  const visitorName = detail
    ? visitorLabel(
        {
          visitorName: detail.visitor?.displayName ?? null,
          visitorEmail: detail.visitor?.email ?? null,
          visitorId: detail.conversation.visitorId,
        },
        lang,
      )
    : "";

  const open = selectedId && detail && detail.conversation.id === selectedId;

  // Only what is ticked *and still in the list*. The list changes under a
  // selection — a filter, a teammate resolving a chat — and a bulk delete must
  // never reach a conversation the operator can no longer see ticked.
  const visiblePicked = useMemo(
    () => (picked === null ? null : new Set(rows.filter((row) => picked.has(row.id)).map((row) => row.id))),
    [picked, rows],
  );

  return (
    <div class={`inbox${selectedId ? " has-selection" : ""}`}>
      <ConversationList
        tab={tab}
        onTab={(next) => {
          resetList();
          setTab(next);
        }}
        counts={counts}
        query={query}
        onQuery={(next) => {
          setLimit(INBOX_PAGE);
          setQuery(next);
        }}
        sites={sites}
        siteId={siteId}
        onSite={(next) => {
          resetList();
          setSiteId(next);
        }}
        tag={tag}
        onTag={(next) => {
          setLimit(INBOX_PAGE);
          setTag(next);
        }}
        rows={rows}
        loading={loading}
        selectedId={selectedId}
        onSelect={(id) => select(id)}
        now={now}
        hasMore={canShowMore(rows.length, limit)}
        onMore={() => {
          setLoading(true);
          setLimit((current) => Math.min(INBOX_MAX, current + INBOX_PAGE));
        }}
        picked={visiblePicked}
        onPicking={(on) => setPicked(on ? new Set() : null)}
        onPick={(id) =>
          setPicked((current) => {
            const next = new Set(current ?? []);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        onPickAll={() => setPicked(new Set(rows.map((row) => row.id)))}
        onBulk={(action) => void bulk(action)}
        bulkBusy={bulkBusy}
        isAdmin={isAdmin}
      />

      <section class="pane conv-pane" aria-label={t.inbox.conversationLabel}>
        {open ? (
          <>
            <ConversationHeader
              detail={detail}
              team={team}
              meId={operator.id}
              isAdmin={isAdmin}
              onDelete={() => {
                void askDeleteConversation(dialogs, t).then((sure) => {
                  if (sure) void erase(() => api.deleteConversation(selectedId), () => t.inbox.deletedToast);
                });
              }}
              onBack={() => select(null)}
              onInfo={() => setInfoOpen(true)}
              onAssign={(id) => {
                // Handing it to someone else is a hand-over: offer them a line of context.
                const member = id !== null && id !== operator.id ? team.find((m) => m.id === id) : undefined;
                if (member) setHandover(member);
                else void run(() => api.assign(selectedId, id));
              }}
              onTicket={() => void openTicket(selectedId)}
              onStatus={(status) =>
                void run(
                  () => api.setStatus(selectedId, status),
                  status === "resolved" ? t.inbox.resolvedToast : t.inbox.reopenedToast,
                )
              }
              onTags={(tags) => void run(() => api.setTags(selectedId, tags))}
              onAddTag={() => {
                void askTag(dialogs, t, detail.conversation.tags).then((added) => {
                  if (added) void run(() => api.setTags(selectedId, [...detail.conversation.tags, added]));
                });
              }}
              onTranscript={() =>
                void run(() => api.sendTranscript(selectedId), t.inbox.transcriptSent(detail.visitor?.email ?? ""))
              }
            />
            <Thread
              messages={messages}
              visitorName={visitorName}
              onOpenImage={setViewing}
              hasEarlier={hasEarlier}
              loadingEarlier={loadingEarlier}
              onLoadEarlier={() => void loadEarlier()}
              actions={{
                meId: operator.id,
                isAdmin,
                onEdit: (message) => void editMessage(message),
                onDelete: (message) => void deleteMessage(message),
              }}
            />
            {visitorDraft ? (
              <div class="typing draft" aria-live="off">
                <span>{t.inbox.draft(visitorName)}</span>
                <q>{visitorDraft}</q>
              </div>
            ) : visitorTyping ? (
              <div class="typing">{t.inbox.typing(visitorName)}</div>
            ) : null}
            <Composer
              conversationId={selectedId}
              self={self}
              canned={canned}
              suggestions={suggestions}
              team={team}
              socket={socket}
              onMessage={(m) => {
                // A send that answers after the operator moved on belongs to
                // the conversation it was sent in, not the one now on screen.
                if (m.conversationId === selectedRef.current) setMessages((current) => mergeMessages(current, [m]));
              }}
              onRemove={(clientMsgId) => setMessages((current) => current.filter((m) => m.clientMsgId !== clientMsgId))}
              onError={flash}
            />
          </>
        ) : selectedId ? (
          <div class="empty">{t.common.loading}</div>
        ) : (
          <div class="empty">
            <div>
              <strong>{t.inbox.pickTitle}</strong>
              {t.inbox.pickHint}
            </div>
          </div>
        )}
      </section>

      {open ? (
        <ContextPanel
          detail={detail}
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          onOpenPast={(id) => select(id)}
          isAdmin={isAdmin}
          onBan={() => {
            const visitor = detail.visitor;
            if (!visitor) return;
            void askBan(dialogs, t).then((reason) => {
              if (reason !== null) void run(() => api.banVisitor(visitor.id, reason), t.inbox.bannedToast);
            });
          }}
          onUnban={() => {
            if (!detail.visitor) return;
            void run(() => api.unbanVisitor(detail.visitor!.id), t.inbox.unbannedToast);
          }}
          onPurge={() => {
            const visitor = detail.visitor;
            if (!visitor) return;
            void askPurge(dialogs, t, visitorName).then((sure) => {
              if (sure) void erase(() => api.purgeVisitor(visitor.id), (res) => t.inbox.purgedToast(res.conversations));
            });
          }}
          onEditVisitor={() => {
            const visitor = detail.visitor;
            if (!visitor) return;
            void askVisitorDetails(dialogs, t, visitor).then((next) => {
              if (next) void run(() => api.updateVisitor(visitor.id, next), t.inbox.visitorSaved);
            });
          }}
        />
      ) : (
        <aside class="pane context" aria-hidden="true" />
      )}

      {viewing ? <ImageViewer item={viewing} onClose={() => setViewing(null)} /> : null}
      {handover && selectedId ? (
        <HandoverDialog
          name={handover.displayName}
          onCancel={() => setHandover(null)}
          onConfirm={(note) => {
            const target = handover;
            setHandover(null);
            void run(() => api.assign(selectedId, target.id, note || undefined), t.inbox.handedOver(target.displayName));
          }}
        />
      ) : null}
      {dialogs.dialog}
      {toast ? (
        <div class={`alert toast${toast.tone === "error" ? " error" : ""}`} role="status">
          {toast.text}
        </div>
      ) : null}
    </div>
  );
}
